import {
    BadgeOutlined,
    EditOutlined,
    ManageAccountsOutlined,
    StarRounded,
} from '@mui/icons-material';
import {
    Box,
    Chip,
    IconButton,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';

import Result from 'components/v2/common/Result';
import TableSkeleton from 'components/v2/common/tableSkeleton';

import useTranslate from 'hooks/useTranslate';
import type {
    AdminConsultant,
    AdminConsultantRole,
    ConsultantLicense,
    ConsultantServiceProviderAssignment,
} from 'types';
import {
    getAdminConsultantName,
    getAdminConsultantRoleLabelKey,
} from 'views/v2/adminPanel/helpers/adminConsultants';

interface ConsultantsTableProps {
    consultants: AdminConsultant[];
    loading?: boolean;
    onEdit: (consultant: AdminConsultant) => void;
    onManageProviders: (consultant: AdminConsultant) => void;
}

const denseCellSx = { px: 1.5, py: 0.75 };

const roleChipSx: Record<AdminConsultantRole, object> = {
    CONSULTANT: {
        bgcolor: 'rgba(14, 165, 233, 0.1)',
        color: '#0369a1',
    },
    ADMIN: {
        bgcolor: 'rgba(99, 102, 241, 0.1)',
        color: '#4338ca',
    },
    SUPER_ADMIN: {
        bgcolor: 'rgba(131, 83, 226, 0.12)',
        color: '#6d28d9',
    },
};

function RoleChip({ role }: { role: AdminConsultantRole }) {
    const { translate } = useTranslate();

    return (
        <Chip
            size="small"
            label={translate(getAdminConsultantRoleLabelKey(role))}
            sx={{
                height: 24,
                borderRadius: 1,
                fontSize: '0.75rem',
                fontWeight: 700,
                ...roleChipSx[role],
            }}
        />
    );
}

function LicenseSummary({ licenses }: { licenses: ConsultantLicense[] }) {
    const { translate } = useTranslate();
    const primaryLicense = licenses[0];

    if (!primaryLicense) {
        return (
            <Typography variant="body2" color="text.secondary">
                {translate('noLicenseDetails')}
            </Typography>
        );
    }

    return (
        <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', minWidth: 0 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                {primaryLicense.country} {primaryLicense.license_number}
            </Typography>
            {licenses.length > 1 ? (
                <Chip
                    size="small"
                    label={`+${licenses.length - 1}`}
                    sx={{ height: 20, fontSize: '0.7rem' }}
                />
            ) : null}
        </Stack>
    );
}

function ProviderSummary({ assignments }: { assignments: ConsultantServiceProviderAssignment[] }) {
    const { translate } = useTranslate();

    if (!assignments.length) {
        return (
            <Typography variant="body2" color="text.secondary">
                {translate('noServiceProvidersAssigned')}
            </Typography>
        );
    }

    const visibleAssignments = assignments.slice(0, 3);
    const tooltip = assignments
        .map((assignment) =>
            assignment.is_default_consultant
                ? `${assignment.company_name} (${translate('companyDefault')})`
                : assignment.company_name
        )
        .join(', ');

    return (
        <Tooltip title={tooltip} arrow>
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                {visibleAssignments.map((assignment) => (
                    <Chip
                        key={assignment.service_provider_id}
                        size="small"
                        icon={assignment.is_default_consultant ? <StarRounded /> : undefined}
                        label={assignment.company_name}
                        color={assignment.is_default_consultant ? 'primary' : 'default'}
                        variant={assignment.is_default_consultant ? 'filled' : 'outlined'}
                        sx={{
                            maxWidth: 240,
                            height: 24,
                            '& .MuiChip-label': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            },
                        }}
                    />
                ))}
                {assignments.length > visibleAssignments.length ? (
                    <Chip
                        size="small"
                        label={`+${assignments.length - visibleAssignments.length}`}
                        variant="outlined"
                        sx={{ height: 24 }}
                    />
                ) : null}
            </Stack>
        </Tooltip>
    );
}

function ConsultantActions({
    consultant,
    onEdit,
    onManageProviders,
}: {
    consultant: AdminConsultant;
    onEdit: ConsultantsTableProps['onEdit'];
    onManageProviders: ConsultantsTableProps['onManageProviders'];
}) {
    const { translate } = useTranslate();
    const name = getAdminConsultantName(consultant);
    const canEdit = consultant.service_providers.length > 0;

    return (
        <Stack direction="row" spacing={0.25} sx={{ justifyContent: 'flex-end' }}>
            <Tooltip
                title={
                    canEdit ? translate('editConsultant') : translate('assignProviderBeforeEditing')
                }
            >
                <span>
                    <IconButton
                        size="small"
                        disabled={!canEdit}
                        aria-label={`${translate('editConsultant')} ${name}`}
                        onClick={() => onEdit(consultant)}
                    >
                        <EditOutlined sx={{ fontSize: 19 }} />
                    </IconButton>
                </span>
            </Tooltip>
            <Tooltip title={translate('manageConsultantServiceProviders')}>
                <IconButton
                    size="small"
                    aria-label={`${translate('manageConsultantServiceProviders')} ${name}`}
                    onClick={() => onManageProviders(consultant)}
                >
                    <ManageAccountsOutlined sx={{ fontSize: 19 }} />
                </IconButton>
            </Tooltip>
        </Stack>
    );
}

export function ConsultantsTable({
    consultants,
    loading = false,
    onEdit,
    onManageProviders,
}: ConsultantsTableProps) {
    const { translate } = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    if (loading) {
        if (!isMobile) return <TableSkeleton tableColumns={6} />;

        return (
            <Stack spacing={1}>
                {Array.from({ length: 4 }).map((_, index) => (
                    <Paper key={index} variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                        <Skeleton width="48%" />
                        <Skeleton width="72%" />
                        <Skeleton width="58%" />
                    </Paper>
                ))}
            </Stack>
        );
    }

    if (!consultants.length) {
        return (
            <Result
                icon={<BadgeOutlined sx={{ fontSize: 32 }} />}
                title={translate('noConsultantsFound')}
                description={translate('tryAdjustingConsultantFilters')}
            />
        );
    }

    if (isMobile) {
        return (
            <Stack spacing={1}>
                {consultants.map((consultant) => (
                    <Paper
                        key={consultant.id}
                        variant="outlined"
                        sx={{ p: 1.5, borderRadius: 1.5, minWidth: 0 }}
                    >
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" noWrap sx={{ fontWeight: 700 }}>
                                    {getAdminConsultantName(consultant)}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', wordBreak: 'break-word' }}
                                >
                                    {consultant.email}
                                </Typography>
                            </Box>
                            <RoleChip role={consultant.role} />
                        </Stack>
                        <Stack spacing={1.25} sx={{ mt: 1.25 }}>
                            <LicenseSummary licenses={consultant.licenses} />
                            <ProviderSummary assignments={consultant.service_providers} />
                        </Stack>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.75 }}>
                            <ConsultantActions
                                consultant={consultant}
                                onEdit={onEdit}
                                onManageProviders={onManageProviders}
                            />
                        </Box>
                    </Paper>
                ))}
            </Stack>
        );
    }

    return (
        <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ borderRadius: 1.5, overflowX: 'auto' }}
        >
            <Table
                size="small"
                sx={{
                    minWidth: 1300,
                    tableLayout: 'fixed',
                    '& .MuiTableCell-root': denseCellSx,
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: 220, fontWeight: 700 }}>
                            {translate('consultant')}
                        </TableCell>
                        <TableCell sx={{ width: 260, fontWeight: 700 }}>
                            {translate('email')}
                        </TableCell>
                        <TableCell sx={{ width: 175, fontWeight: 700 }}>
                            {translate('role')}
                        </TableCell>
                        <TableCell sx={{ width: 230, fontWeight: 700 }}>
                            {translate('licenseDetails')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                            {translate('serviceProviders')}
                        </TableCell>
                        <TableCell align="right" sx={{ width: 88 }} />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {consultants.map((consultant) => (
                        <TableRow key={consultant.id} hover>
                            <TableCell>
                                <Typography variant="body2" noWrap sx={{ fontWeight: 700 }}>
                                    {getAdminConsultantName(consultant)}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Tooltip title={consultant.email} arrow>
                                    <Typography variant="body2" noWrap>
                                        {consultant.email}
                                    </Typography>
                                </Tooltip>
                            </TableCell>
                            <TableCell>
                                <RoleChip role={consultant.role} />
                            </TableCell>
                            <TableCell>
                                <LicenseSummary licenses={consultant.licenses} />
                            </TableCell>
                            <TableCell>
                                <ProviderSummary assignments={consultant.service_providers} />
                            </TableCell>
                            <TableCell align="right">
                                <ConsultantActions
                                    consultant={consultant}
                                    onEdit={onEdit}
                                    onManageProviders={onManageProviders}
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export default ConsultantsTable;
